import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Clock, ArrowRight } from 'lucide-react';

export default function Register() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!agreedToTerms) {
            toast.error('Please agree to the Terms of Use to continue');
            return;
        }
        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        setIsLoading(true);
        try {
            await register(email, password, name);
            toast.success('Account created successfully!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-slate-50 flex flex-col">
            {/* Hero Section */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                {/* Logo */}
                <div className="flex flex-col items-center mb-6">
                    <img 
                        src="/voilaw-logo.png" 
                        alt="Voilaw Logo" 
                        className="h-32 w-32 object-contain"
                    />
                    <h1 className="font-brand text-3xl text-foreground mt-4">Voilaw</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Time & task tracking for attorneys
                    </p>
                </div>

                {/* Register Form */}
                <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name" className="text-foreground">Full Name</Label>
                        <Input
                            id="name"
                            type="text"
                            placeholder="John Smith"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            data-testid="register-name-input"
                            className="h-14 rounded-2xl bg-white shadow-soft border-0 text-lg px-5"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="email" className="text-foreground">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            placeholder="attorney@lawfirm.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            data-testid="register-email-input"
                            className="h-14 rounded-2xl bg-white shadow-soft border-0 text-lg px-5"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="password" className="text-foreground">Password</Label>
                        <Input
                            id="password"
                            type="password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            data-testid="register-password-input"
                            className="h-14 rounded-2xl bg-white shadow-soft border-0 text-lg px-5"
                        />
                        <p className="text-xs text-muted-foreground px-1">
                            Minimum 6 characters
                        </p>
                    </div>
                    
                    {/* Terms of Use Checkbox */}
                    <div className="flex items-start gap-3 pt-2">
                        <Checkbox
                            id="terms"
                            checked={agreedToTerms}
                            onCheckedChange={setAgreedToTerms}
                            className="mt-0.5 min-w-[18px] min-h-[18px]"
                            data-testid="terms-checkbox"
                        />
                        <label htmlFor="terms" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                            I agree to the{' '}
                            <Link 
                                to="/terms" 
                                className="text-[#1C6EE7] hover:underline font-medium"
                                onClick={(e) => e.stopPropagation()}
                            >
                                Terms of Use
                            </Link>
                        </label>
                    </div>
                    
                    <div className="pt-4">
                        <Button
                            type="submit"
                            className="w-full sm:w-3/4 mx-auto block h-14 rounded-full text-lg font-semibold bg-[#1C6EE7] hover:bg-[#1559c7] shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                            disabled={isLoading || !agreedToTerms}
                            data-testid="register-submit-btn"
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Clock className="h-5 w-5 animate-spin" />
                                    Creating account...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    Create account
                                    <ArrowRight className="h-5 w-5" />
                                </span>
                            )}
                        </Button>
                    </div>
                </form>

                <div className="mt-8 text-center">
                    <p className="text-muted-foreground">
                        Already have an account?{' '}
                        <Link
                            to="/login"
                            className="text-primary font-semibold hover:underline"
                            data-testid="login-link"
                        >
                            Sign in
                        </Link>
                    </p>
                </div>
                
                {/* Footer with Terms Link */}
                <div className="mt-8 pt-6 border-t border-slate-200/50 w-full max-w-sm">
                    <p className="text-xs text-center text-slate-400">
                        By creating an account, you agree to our{' '}
                        <Link to="/terms" className="text-slate-500 hover:text-[#1C6EE7] hover:underline">
                            Terms of Use
                        </Link>
                        {' '}and{' '}
                        <Link to="/privacy" className="text-slate-500 hover:text-[#1C6EE7] hover:underline">
                            Privacy Policy
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
